import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import PageHeader from "../layout/PageHeader";
import {
  deleteAiKathyCall,
  fetchAiKathyCallDetail,
  fetchAiKathyCalls,
  updateAiKathyCall,
} from "../../utils/api";
import "../aiHiring/AiHiring.css";
import "./AiKathy.css";

const formatWhen = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatWhenShort = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  const now = new Date();
  const sameDay =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  if (sameDay) {
    return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatDuration = (seconds) => {
  const n = Number(seconds) || 0;
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  const s = n % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
};

const formatPhoneDisplay = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw || "Unknown caller";
};

const AGENT_ROLES = new Set([
  "agent",
  "assistant",
  "bot",
  "system",
  "kathy",
  "ai",
]);

const USER_ROLES = new Set(["user", "caller", "caregiver", "human", "client"]);

const HISTORY_RANGES = [
  { id: "7d", label: "7 days", days: 7 },
  { id: "14d", label: "14 days", days: 14 },
  { id: "30d", label: "30 days", days: 30 },
  { id: "all", label: "All", days: null },
];

const CHART_DAYS = 14;

function toDayKey(value) {
  const d = new Date(value || "");
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function dayKeyLabel(dayKey) {
  if (!dayKey) return "Unknown date";
  const d = new Date(`${dayKey}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dayKey;
  const now = new Date();
  const todayKey = toDayKey(now);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = toDayKey(yesterday);
  if (dayKey === todayKey) return "Today";
  if (dayKey === yesterdayKey) return "Yesterday";
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

function filterCallsByHistoryRange(rows, rangeId) {
  const cfg = HISTORY_RANGES.find((r) => r.id === rangeId) || HISTORY_RANGES[1];
  if (!cfg.days) return rows;
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - (cfg.days - 1));
  return rows.filter((row) => {
    const t = new Date(row.occurredAt || "").getTime();
    return Number.isFinite(t) && t >= cutoff.getTime();
  });
}

function buildDailyChartSeries(rows, dayCount = CHART_DAYS) {
  const buckets = new Map();
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (dayCount - 1));

  for (let i = 0; i < dayCount; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const key = toDayKey(d);
    buckets.set(key, {
      dayKey: key,
      label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      calls: 0,
      minutes: 0,
    });
  }

  for (const row of rows) {
    const key = toDayKey(row.occurredAt);
    if (!buckets.has(key)) continue;
    const b = buckets.get(key);
    b.calls += 1;
    b.minutes += Math.round((Number(row.durationSeconds) || 0) / 60);
  }

  return Array.from(buckets.values());
}

function groupCallsByDay(rows) {
  const map = new Map();
  for (const row of rows) {
    const key = toDayKey(row.occurredAt) || "unknown";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  }
  return Array.from(map.entries())
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([dayKey, dayCalls]) => ({
      dayKey,
      label: dayKeyLabel(dayKey),
      calls: dayCalls.sort(
        (a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
      ),
    }));
}

function KathyChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  return (
    <div className="ai-kathy-chart-tip">
      <div className="ai-kathy-chart-tip-title">{label}</div>
      <div className="ai-kathy-chart-tip-row">
        Calls: <strong>{point?.calls ?? 0}</strong>
      </div>
      <div className="ai-kathy-chart-tip-row">
        Talk time: <strong>{point?.minutes ?? 0} min</strong>
      </div>
    </div>
  );
}

function chartDaysForRange(rangeId) {
  if (rangeId === "7d") return 7;
  if (rangeId === "30d") return 30;
  if (rangeId === "all") return 30;
  return CHART_DAYS;
}

function AiKathyInsights({ calls, historyRange, onHistoryRangeChange }) {
  const chartDays = chartDaysForRange(historyRange);
  const chartData = useMemo(
    () => buildDailyChartSeries(calls, chartDays),
    [calls, chartDays],
  );
  const hasActivity = chartData.some((d) => d.calls > 0);

  return (
    <div className="ai-kathy-insights">
      <div className="ai-kathy-insights-head">
        <div>
          <h3>Activity & history</h3>
          <p>
            Call volume and talk time — last {chartDays} day{chartDays === 1 ? "" : "s"} in view.
          </p>
        </div>
        <div className="ai-kathy-history-tabs" role="tablist" aria-label="History range">
          {HISTORY_RANGES.map((r) => (
            <button
              key={r.id}
              type="button"
              role="tab"
              aria-selected={historyRange === r.id}
              className={`ai-kathy-history-tab ${historyRange === r.id ? "active" : ""}`}
              onClick={() => onHistoryRangeChange(r.id)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="ai-kathy-charts-grid">
        <div className="ai-kathy-chart-card">
          <h4>Inbound calls</h4>
          <p className="ai-kathy-chart-sub">Daily call count</p>
          {hasActivity ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} width={32} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip content={<KathyChartTooltip />} cursor={{ fill: "rgba(99, 102, 241, 0.08)" }} />
                <Bar dataKey="calls" fill="#6366f1" radius={[6, 6, 0, 0]} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="ai-kathy-chart-empty">No calls in this window yet.</p>
          )}
        </div>

        <div className="ai-kathy-chart-card">
          <h4>Talk time</h4>
          <p className="ai-kathy-chart-sub">Minutes per day</p>
          {hasActivity ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="kathyMinutesFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#818cf8" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#818cf8" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} width={32} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip content={<KathyChartTooltip />} />
                <Area
                  type="monotone"
                  dataKey="minutes"
                  stroke="#4f46e5"
                  strokeWidth={2}
                  fill="url(#kathyMinutesFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="ai-kathy-chart-empty">No talk time recorded yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function parseTranscriptTurns(text) {
  const raw = String(text || "").trim();
  if (!raw) return [];

  const lines = raw.split("\n").map((l) => l.trim()).filter(Boolean);
  const turns = [];

  for (const line of lines) {
    const match = line.match(/^([^:]{1,40}):\s*(.+)$/);
    if (match) {
      turns.push({ role: match[1].trim(), text: match[2].trim() });
    } else {
      turns.push({ role: "Transcript", text: line });
    }
  }

  return turns;
}

function roleBubbleClass(role) {
  const key = String(role || "").toLowerCase();
  if (USER_ROLES.has(key)) return "user";
  if (AGENT_ROLES.has(key)) return "agent";
  if (key.includes("kathy") || key.includes("assistant")) return "agent";
  return "agent";
}

function TranscriptChat({ transcriptText }) {
  const turns = useMemo(() => parseTranscriptTurns(transcriptText), [transcriptText]);

  if (!turns.length) {
    return (
      <p style={{ color: "#64748b", fontSize: 14, margin: 0 }}>
        No transcript yet. If you just finished a call, wait a minute and refresh — ElevenLabs
        sends the transcript when the call ends.
      </p>
    );
  }

  return (
    <>
      {turns.map((turn, idx) => {
        const kind = roleBubbleClass(turn.role);
        const label =
          kind === "user" ? "Caller" : turn.role.toLowerCase() === "agent" ? "Kathy" : turn.role;
        return (
          <div
            key={`${idx}-${turn.role}`}
            className={`ai-kathy-bubble ai-kathy-bubble--${kind}`}
          >
            <span className="ai-kathy-bubble-role">{label}</span>
            {turn.text}
          </div>
        );
      })}
    </>
  );
}

function EditConversationModal({ call, onClose, onSaved }) {
  const [summary, setSummary] = useState(call?.summary || "");
  const [transcriptText, setTranscriptText] = useState(call?.transcriptText || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setSummary(call?.summary || "");
    setTranscriptText(call?.transcriptText || "");
    setErr("");
  }, [call]);

  if (!call) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      const data = await updateAiKathyCall(call.id, { summary, transcriptText });
      onSaved(data);
      onClose();
    } catch (error) {
      console.error(error);
      setErr("Could not save changes. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ai-kathy-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="ai-kathy-edit-modal"
        role="dialog"
        aria-labelledby="ai-kathy-edit-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ai-kathy-edit-modal-header">
          <h3 id="ai-kathy-edit-title">Edit conversation</h3>
          <p>{formatPhoneDisplay(call.phoneNumber)}</p>
        </div>
        <form onSubmit={submit}>
          <label className="ai-kathy-edit-label" htmlFor="ai-kathy-edit-summary">
            Summary
          </label>
          <textarea
            id="ai-kathy-edit-summary"
            className="ai-kathy-edit-textarea"
            rows={4}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="AI summary or your notes…"
          />
          <label className="ai-kathy-edit-label" htmlFor="ai-kathy-edit-transcript">
            Transcript
          </label>
          <textarea
            id="ai-kathy-edit-transcript"
            className="ai-kathy-edit-textarea ai-kathy-edit-textarea--mono"
            rows={10}
            value={transcriptText}
            onChange={(e) => setTranscriptText(e.target.value)}
            placeholder="Role: message lines…"
          />
          {err ? <p className="ai-kathy-edit-error">{err}</p> : null}
          <div className="ai-kathy-edit-actions">
            <button type="button" className="ai-kathy-btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="ai-kathy-btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DetailPanel({ detail, detailLoading, onClose, inline, onEdit, onDelete }) {
  if (detailLoading) {
    return (
      <div className={`ai-kathy-detail-panel ${inline ? "ai-kathy-detail-panel--inline" : ""}`}>
        <div className="ai-kathy-skeleton">Loading conversation…</div>
      </div>
    );
  }

  if (detail?.error) {
    return (
      <div className={`ai-kathy-detail-panel ${inline ? "ai-kathy-detail-panel--inline" : ""}`}>
        <div className="ai-kathy-placeholder-detail">{detail.error}</div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className={`ai-kathy-detail-panel ${inline ? "ai-kathy-detail-panel--inline" : ""}`}>
        <div className="ai-kathy-placeholder-detail">
          <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
          <p>Select a call to read the full conversation with Kathy.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`ai-kathy-detail-panel ${inline ? "ai-kathy-detail-panel--inline" : ""}`}>
      <div className="ai-kathy-detail-header">
        <div className="ai-kathy-detail-header-row">
          <h2>{formatPhoneDisplay(detail.phoneNumber)}</h2>
          <div className="ai-kathy-row-actions">
            {onEdit ? (
              <button
                type="button"
                className="ai-kathy-icon-btn"
                title="Edit conversation"
                aria-label="Edit conversation"
                onClick={() => onEdit(detail)}
              >
                ✎
              </button>
            ) : null}
            {onDelete ? (
              <button
                type="button"
                className="ai-kathy-icon-btn ai-kathy-icon-btn--danger"
                title="Delete conversation"
                aria-label="Delete conversation"
                onClick={() => onDelete(detail)}
              >
                🗑
              </button>
            ) : null}
          </div>
        </div>
        <div className="ai-kathy-detail-chips">
          <span className="ai-kathy-chip">{formatWhen(detail.occurredAt)}</span>
          <span className="ai-kathy-chip">{formatDuration(detail.durationSeconds)}</span>
          <span className="ai-kathy-chip">Completed</span>
        </div>
        {onClose ? (
          <button
            type="button"
            className="button secondary-button btn-sm"
            style={{ marginTop: 12 }}
            onClick={onClose}
          >
            ← Back to list
          </button>
        ) : null}
      </div>

      {detail.summary ? (
        <div className="ai-kathy-summary-box">
          <h4>AI summary</h4>
          <p>{detail.summary}</p>
        </div>
      ) : null}

      <div className="ai-kathy-transcript-scroll">
        <TranscriptChat transcriptText={detail.transcriptText} />
      </div>
    </div>
  );
}

export default function AiKathyPage(props) {
  const { activeLocation } = props;
  const locationId = activeLocation?._id || activeLocation?.id || "";
  const locationName = activeLocation?.name || "this location";

  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 960,
  );
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [historyRange, setHistoryRange] = useState("14d");

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 960);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const loadCalls = useCallback(async (silent = false) => {
    if (!locationId) {
      setCalls([]);
      setLoading(false);
      return;
    }
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const data = await fetchAiKathyCalls(locationId, 100);
      const rows = Array.isArray(data?.calls) ? data.calls : [];
      setCalls(rows);
    } catch (e) {
      console.error(e);
      setError("Could not load conversations. Try Refresh.");
      setCalls([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [locationId]);

  useEffect(() => {
    setSelectedId(null);
    setDetail(null);
    loadCalls(false);
  }, [locationId, loadCalls]);

  useEffect(() => {
    if (selectedId || !calls.length || isNarrow) return;
    openDetail(calls[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auto-open latest on desktop only once per load
  }, [calls, isNarrow, selectedId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === "visible" && locationId) {
        loadCalls(true);
      }
    }, 45000);
    return () => clearInterval(interval);
  }, [loadCalls, locationId]);

  const historyCalls = useMemo(
    () => filterCallsByHistoryRange(calls, historyRange),
    [calls, historyRange],
  );

  const filteredCalls = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return historyCalls;
    return historyCalls.filter((row) => {
      const hay = [
        row.phoneNumber,
        row.summary,
        row.transcriptPreview,
        formatWhen(row.occurredAt),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [historyCalls, search]);

  const historyGroups = useMemo(() => groupCallsByDay(filteredCalls), [filteredCalls]);

  const stats = useMemo(() => {
    const total = historyCalls.length;
    const latest = historyCalls[0]?.occurredAt;
    const totalSeconds = historyCalls.reduce(
      (sum, c) => sum + (Number(c.durationSeconds) || 0),
      0,
    );
    return { total, latest, totalSeconds };
  }, [historyCalls]);

  const openDetail = async (callId) => {
    setSelectedId(callId);
    setDetail(null);
    setDetailLoading(true);
    try {
      const data = await fetchAiKathyCallDetail(callId);
      setDetail(data);
    } catch (e) {
      console.error(e);
      setDetail({ error: "Failed to load transcript." });
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
  };

  const beginEdit = async (row) => {
    if (row?.transcriptText !== undefined) {
      setEditTarget(row);
      return;
    }
    try {
      setDetailLoading(true);
      const data = await fetchAiKathyCallDetail(row.id);
      setEditTarget(data);
    } catch (e) {
      console.error(e);
      setError("Could not load conversation for editing.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEditSaved = (updated) => {
    setCalls((prev) =>
      prev.map((c) =>
        c.id === updated.id
          ? {
              ...c,
              summary: updated.summary,
              transcriptPreview: String(updated.transcriptText || "").slice(0, 280),
              hasFullTranscript: Boolean(updated.transcriptText),
            }
          : c,
      ),
    );
    if (selectedId === updated.id) {
      setDetail(updated);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleteBusy(true);
    setError("");
    try {
      await deleteAiKathyCall(deleteTarget.id);
      setCalls((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      if (selectedId === deleteTarget.id) {
        closeDetail();
      }
      setDeleteTarget(null);
    } catch (e) {
      console.error(e);
      setError("Could not delete conversation.");
    } finally {
      setDeleteBusy(false);
    }
  };

  const showList = !isNarrow || !selectedId;
  const showDetailPanel = !isNarrow || Boolean(selectedId);

  return (
    <div className="page-shell ai-hiring-page ai-kathy-page">
      <PageHeader
        title="AI Kathy"
        subtitle={`Inbound phone conversations · ${locationName}`}
        icon={
          <span role="img" aria-label="AI Kathy">
            📞
          </span>
        }
        {...props}
      />

      <div className="ai-hiring-content">
        <div className="ai-kathy-toolbar">
          <input
            type="search"
            className="ai-kathy-search"
            placeholder="Search phone, summary, or transcript…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={!locationId || loading}
          />
          <div className="ai-kathy-toolbar-actions">
            <button
              type="button"
              className="ai-kathy-refresh-btn"
              disabled={!locationId || refreshing}
              onClick={() => loadCalls(true)}
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        {!locationId ? (
          <div className="ai-kathy-empty">
            <div className="ai-kathy-empty-icon">📍</div>
            <h3>Select a location</h3>
            <p>Choose an office in the header to see Kathy calls for that site.</p>
          </div>
        ) : loading ? (
          <div className="ai-kathy-empty">
            <div className="ai-kathy-skeleton">Loading conversations…</div>
          </div>
        ) : (
          <>
            {error ? <div className="ai-alert ai-alert-error">{error}</div> : null}

            <AiKathyInsights
              calls={historyCalls}
              historyRange={historyRange}
              onHistoryRangeChange={setHistoryRange}
            />

            <div className="ai-kathy-stats">
              <div className="ai-kathy-stat">
                <p className="ai-kathy-stat-label">Calls in range</p>
                <p className="ai-kathy-stat-value">{stats.total}</p>
                <p className="ai-kathy-stat-foot">
                  {HISTORY_RANGES.find((r) => r.id === historyRange)?.label || "Selected period"} ·{" "}
                  {locationName}
                </p>
              </div>
              <div className="ai-kathy-stat">
                <p className="ai-kathy-stat-label">Talk time</p>
                <p className="ai-kathy-stat-value">{formatDuration(stats.totalSeconds)}</p>
                <p className="ai-kathy-stat-foot">All recorded calls</p>
              </div>
              <div className="ai-kathy-stat">
                <p className="ai-kathy-stat-label">Latest call</p>
                <p className="ai-kathy-stat-value" style={{ fontSize: stats.latest ? 18 : 26 }}>
                  {stats.latest ? formatWhenShort(stats.latest) : "—"}
                </p>
                <p className="ai-kathy-stat-foot">
                  {stats.latest ? formatWhen(stats.latest) : "Waiting for first call"}
                </p>
              </div>
            </div>

            {filteredCalls.length === 0 ? (
              <div className="ai-kathy-empty">
                <div className="ai-kathy-empty-icon">🎙️</div>
                <h3>No conversations yet</h3>
                <p>
                  Call your Kathy number, finish the call, then refresh. Transcripts usually appear
                  within a minute.
                </p>
              </div>
            ) : (
              <div className="ai-kathy-layout">
                {showList ? (
                  <div className="ai-kathy-list-panel">
                    <div className="ai-kathy-list-header">
                      <h3>History</h3>
                      <p>
                        {filteredCalls.length} conversation{filteredCalls.length === 1 ? "" : "s"}{" "}
                        · grouped by day
                      </p>
                    </div>
                    <div className="ai-kathy-call-list">
                      {historyGroups.map((group) => (
                        <section key={group.dayKey} className="ai-kathy-history-group">
                          <div className="ai-kathy-history-day">
                            <span>{group.label}</span>
                            <span className="ai-kathy-history-day-count">
                              {group.calls.length} call{group.calls.length === 1 ? "" : "s"}
                            </span>
                          </div>
                          {group.calls.map((row) => (
                            <div
                              key={row.id}
                              className={`ai-kathy-call-item ${selectedId === row.id ? "active" : ""}`}
                            >
                              <button
                                type="button"
                                className="ai-kathy-call-item-main"
                                onClick={() => openDetail(row.id)}
                              >
                                <div className="ai-kathy-call-row">
                                  <div className="ai-kathy-avatar" aria-hidden>
                                    K
                                  </div>
                                  <div className="ai-kathy-call-meta">
                                    <div className="ai-kathy-call-top">
                                      <span className="ai-kathy-call-phone">
                                        {formatPhoneDisplay(row.phoneNumber)}
                                      </span>
                                      <span className="ai-kathy-call-time">
                                        {formatWhenShort(row.occurredAt)}
                                      </span>
                                    </div>
                                    <p className="ai-kathy-call-preview">
                                      {row.summary ||
                                        row.transcriptPreview ||
                                        "Tap to view transcript"}
                                    </p>
                                    <span className="ai-kathy-duration-pill">
                                      {formatDuration(row.durationSeconds)}
                                    </span>
                                  </div>
                                </div>
                              </button>
                              <div className="ai-kathy-row-actions ai-kathy-row-actions--list">
                                <button
                                  type="button"
                                  className="ai-kathy-icon-btn"
                                  title="Edit"
                                  aria-label="Edit conversation"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    beginEdit(row);
                                  }}
                                >
                                  ✎
                                </button>
                                <button
                                  type="button"
                                  className="ai-kathy-icon-btn ai-kathy-icon-btn--danger"
                                  title="Delete"
                                  aria-label="Delete conversation"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteTarget(row);
                                  }}
                                >
                                  🗑
                                </button>
                              </div>
                            </div>
                          ))}
                        </section>
                      ))}
                    </div>
                  </div>
                ) : null}

                {showDetailPanel ? (
                  <DetailPanel
                    detail={detail}
                    detailLoading={detailLoading}
                    inline
                    onClose={isNarrow ? closeDetail : undefined}
                    onEdit={beginEdit}
                    onDelete={(d) => setDeleteTarget(d)}
                  />
                ) : null}
              </div>
            )}
          </>
        )}
      </div>

      {editTarget ? (
        <EditConversationModal
          call={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={handleEditSaved}
        />
      ) : null}

      {deleteTarget ? (
        <div
          className="ai-kathy-modal-backdrop"
          role="presentation"
          onClick={() => !deleteBusy && setDeleteTarget(null)}
        >
          <div
            className="ai-kathy-delete-modal"
            role="alertdialog"
            aria-labelledby="ai-kathy-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="ai-kathy-delete-title">Delete conversation?</h3>
            <p>
              This removes the call for{" "}
              <strong>{formatPhoneDisplay(deleteTarget.phoneNumber)}</strong> from AI Kathy.
              This cannot be undone.
            </p>
            <div className="ai-kathy-edit-actions">
              <button
                type="button"
                className="ai-kathy-btn-ghost"
                disabled={deleteBusy}
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ai-kathy-btn-danger"
                disabled={deleteBusy}
                onClick={confirmDelete}
              >
                {deleteBusy ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
